import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Timer, ChevronLeft, ChevronRight, Trophy, CheckCircle2, XCircle, RotateCcw, Sparkles } from "lucide-react";

/**
 * StoryQuizPlayer — reusable full-screen quiz player with story-book UX.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   quiz       — { title, questions: [{ question, options?, correct_answer, explanation? }] }
 *              — question types supported: mcq (has options), short_answer/fill_blank/true_false (no options, string answer)
 *   timePerQ   — seconds per question (0 = untimed). Defaults to 30.
 *
 * Layout:  one question per "page" · Framer Motion slide transitions · per-question
 * countdown pill · finish → review panel showing student answer vs. correct answer +
 * explanation for every question with color-coded pass/fail.
 */
export default function StoryQuizPlayer({ open, onClose, quiz, timePerQ = 30 }) {
  const [stage, setStage] = useState("play"); // play | done
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [qTimeLeft, setQTimeLeft] = useState(timePerQ);

  const total = quiz?.questions?.length || 0;
  const q = quiz?.questions?.[idx];

  useEffect(() => {
    if (!open) return;
    setStage("play"); setIdx(0); setAnswers({}); setElapsed(0);
    setQTimeLeft(timePerQ);
  }, [open, quiz?.id, timePerQ]);

  // Global elapsed timer
  useEffect(() => {
    if (!open || stage !== "play") return;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [open, stage]);

  // Per-question countdown → auto-advance when it hits 0
  useEffect(() => {
    if (!open || stage !== "play" || !timePerQ) return;
    setQTimeLeft(timePerQ);
    const t = setInterval(() => {
      setQTimeLeft((s) => {
        if (s <= 1) { clearInterval(t); advance(true); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, open, stage]);

  const advance = (auto = false) => {
    if (idx < total - 1) setIdx(idx + 1);
    else finish(auto);
  };
  const finish = () => setStage("done");
  const setAnswer = (v) => setAnswers((a) => ({ ...a, [idx]: v }));

  const results = useMemo(() => {
    if (!quiz) return [];
    return quiz.questions.map((qq, i) => {
      const given = answers[i];
      const correct = (qq.correct_answer || "").toString().trim();
      const isCorrect = given != null && given.toString().trim().toLowerCase() === correct.toLowerCase();
      return { q: qq, given, correct, isCorrect };
    });
  }, [answers, quiz]);
  const score = results.filter((r) => r.isCorrect).length;
  const pct = total ? Math.round((score / total) * 100) : 0;

  if (!open || !quiz) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-[#0A0C10]/95 backdrop-blur-md flex items-start justify-center overflow-y-auto py-6 px-4" data-testid="story-quiz-player">
      <button
        onClick={onClose}
        data-testid="quiz-player-close"
        className="fixed top-5 right-5 z-10 w-10 h-10 rounded-md bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
        aria-label="Close"
      ><X size={18} /></button>

      <div className="w-full max-w-3xl mt-4">
        {stage === "play" && q && (
          <motion.div key={`play-${idx}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            {/* Top bar */}
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/50">/ {quiz.title || "Quiz"}</div>
                <div className="text-white/80 text-sm font-mono mt-1">Question {idx + 1} of {total}</div>
              </div>
              <div className="flex items-center gap-2">
                {timePerQ > 0 && (
                  <div
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-mono transition-colors ${qTimeLeft <= 5 ? "bg-red-500/20 text-red-300 animate-pulse" : "bg-white/10 text-white"}`}
                    data-testid="quiz-question-timer"
                  >
                    <Timer size={14} /> {qTimeLeft}s
                  </div>
                )}
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 text-white/60 text-xs font-mono">
                  Total {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}
                </div>
              </div>
            </div>

            {/* Progress */}
            <div className="h-1 w-full bg-white/10 rounded overflow-hidden mb-8">
              <motion.div
                className="h-full bg-gradient-to-r from-[#FFD600] via-[#FF7AD9] to-[#7C3AED]"
                initial={{ width: 0 }} animate={{ width: `${((idx + 1) / total) * 100}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>

            {/* Question card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.25 }}
                className="bg-white/[0.03] border border-white/10 rounded-xl p-6 lg:p-10"
              >
                <div className="text-white font-display font-bold text-xl lg:text-2xl leading-snug" data-testid="quiz-question-text">
                  {q.question}
                </div>
                <div className="mt-6 space-y-2.5">
                  {(q.options && q.options.length > 0) ? (
                    q.options.map((opt, oi) => {
                      const active = answers[idx] === opt;
                      return (
                        <button
                          key={oi}
                          onClick={() => setAnswer(opt)}
                          data-testid={`quiz-option-${oi}`}
                          className={`w-full text-left px-4 py-3 rounded-md border transition-all ${active
                            ? "border-[#FFD600] bg-[#FFD600]/15 text-white"
                            : "border-white/10 hover:border-white/30 text-white/80 hover:bg-white/5"}`}
                        >
                          <span className="font-mono text-xs text-white/50 mr-2">{String.fromCharCode(65 + oi)}</span>
                          {opt}
                        </button>
                      );
                    })
                  ) : (
                    <input
                      value={answers[idx] || ""}
                      onChange={(e) => setAnswer(e.target.value)}
                      placeholder="Type your answer…"
                      data-testid="quiz-answer-input"
                      className="w-full px-4 py-3 rounded-md bg-white/5 border border-white/10 focus:border-[#FFD600] outline-none text-white text-lg"
                      autoFocus
                    />
                  )}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Nav */}
            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={() => idx > 0 && setIdx(idx - 1)}
                disabled={idx === 0}
                data-testid="quiz-prev"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 text-sm"
              >
                <ChevronLeft size={14} /> Previous
              </button>
              <button
                onClick={() => advance(false)}
                data-testid="quiz-next"
                className="inline-flex items-center gap-1.5 px-6 py-3 rounded-md bg-[#FFD600] text-[#0A0C10] text-sm font-bold hover:scale-[1.02] transition-transform"
              >
                {idx < total - 1 ? <>Next <ChevronRight size={14} /></> : <>Finish <Trophy size={14} /></>}
              </button>
            </div>
          </motion.div>
        )}

        {stage === "done" && (
          <motion.div key="done" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} data-testid="quiz-review">
            {/* Score header */}
            <div className="bg-gradient-to-br from-[#FFD600]/20 to-[#7C3AED]/20 border border-white/10 rounded-xl p-8 text-center">
              <Trophy size={44} className="text-[#FFD600] mx-auto" />
              <div className="text-white/60 text-[11px] font-mono uppercase tracking-[0.22em] mt-3">/ your score</div>
              <div className="font-display font-bold text-6xl text-white mt-2">{score} <span className="text-white/40">/ {total}</span></div>
              <div className="mt-1 text-2xl font-mono text-[#FFD600]">{pct}%</div>
              <div className="mt-3 text-sm text-white/60">Total time · {Math.floor(elapsed / 60)}m {elapsed % 60}s</div>
            </div>

            {/* Per-question review */}
            <div className="mt-6 space-y-3" data-testid="quiz-review-list">
              {results.map((r, i) => (
                <div
                  key={i}
                  data-testid={`quiz-review-item-${i}`}
                  className={`border rounded-lg p-4 ${r.isCorrect
                    ? "border-emerald-500/40 bg-emerald-500/[0.06]"
                    : "border-red-500/40 bg-red-500/[0.06]"}`}
                >
                  <div className="flex items-start gap-3">
                    {r.isCorrect
                      ? <CheckCircle2 size={18} className="text-emerald-400 mt-0.5 shrink-0" />
                      : <XCircle size={18} className="text-red-400 mt-0.5 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-white font-medium">Q{i + 1}. {r.q.question}</div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[13px]">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md ${r.isCorrect ? "bg-emerald-500/20 text-emerald-200" : "bg-red-500/20 text-red-200"}`}>
                          Your answer: <strong>{r.given != null && r.given !== "" ? r.given : <em className="opacity-80">skipped</em>}</strong>
                        </span>
                        {!r.isCorrect && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-500/20 text-emerald-200">
                            Correct: <strong>{r.correct}</strong>
                          </span>
                        )}
                      </div>
                      {r.q.explanation && (
                        <div className="mt-2 text-xs text-white/70 italic flex items-start gap-1.5">
                          <Sparkles size={11} className="mt-0.5 text-[#FFD600] shrink-0" /> {r.q.explanation}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer actions */}
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              <button
                onClick={() => { setStage("play"); setIdx(0); setAnswers({}); setElapsed(0); setQTimeLeft(timePerQ); }}
                data-testid="quiz-retake"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm"
              >
                <RotateCcw size={14} /> Retake
              </button>
              <button
                onClick={onClose}
                data-testid="quiz-review-close"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#FFD600] text-[#0A0C10] font-bold text-sm"
              >
                Done
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
